import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Save, Printer, Wand2, Mail } from "lucide-react";
import { CONFIGURATOR_PRODUCTS, PRODUCT_OPTIONS, COLORS } from "@/lib/configurator-data";
import { computePrice } from "@/lib/pricing";
import { ElementPreview } from "@/components/ElementPreview";
import { InvoiceSheet } from "./InvoiceSheet";
import { API, inputCls, selectCls, eur } from "./shared";

const emptyItem = () => ({
  product: "Fenster",
  width: "1200",
  height: "1400",
  fence_length: "25",
  fence_height: "1,4 m",
  color: COLORS[0],
  options: {},
  qty: 1,
  unit_price: 0,
});

const INVOICE_STATUSES = [
  ["entwurf", "Entwurf"],
  ["gesendet", "Gesendet"],
  ["bezahlt", "Bezahlt"],
  ["ueberfaellig", "Überfällig"],
  ["storniert", "Storniert"],
];

export const InvoiceEditor = ({ invoice, onClose, onSaved, onPrint }) => {
  const isNew = !invoice?.id;
  const [form, setForm] = useState(
    invoice || {
      customer_name: "",
      customer_email: "",
      customer_address: "",
      items: [emptyItem()],
      note: "Zahlbar innerhalb von 14 Tagen ohne Abzug.",
      status: "entwurf",
      vat_rate: 19,
      invoice_date: new Date().toISOString().slice(0, 10),
      due_date: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
    }
  );
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    axios.get(`${API}/customers`, { withCredentials: true })
      .then(({ data }) => setCustomers(data))
      .catch(() => {});
  }, []);

  const pickCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    if (c) setForm({ ...form, customer_name: c.name, customer_email: c.email, customer_address: c.address });
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setItem = (i, patch) => setForm({ ...form, items: form.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) });
  const addItem = () => setForm({ ...form, items: [...form.items, emptyItem()] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const suggestPrice = (i) => {
    const it = form.items[i];
    const p = computePrice({
      productName: it.product,
      width: it.width,
      height: it.height,
      opts: it.options,
      color: it.color,
      extras: ["Montage durch Ringel-Team"],
      fence: { length: it.fence_length, height: it.fence_height },
    });
    if (p) setItem(i, { unit_price: Math.round(p / 1.19) });
    else toast.error("Bitte zuerst Maße und Ausführung vollständig wählen (oder Preis frei eintragen).");
  };

  const rate = parseInt(form.vat_rate) || 0;
  const totalNet = form.items.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0), 0);
  const totalGross = totalNet * (1 + rate / 100);

  const save = async (printAfter = false) => {
    if (!form.customer_name.trim()) {
      toast.error("Bitte Kundenname angeben.");
      return null;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        vat_rate: rate,
        items: form.items.map((it) => ({ ...it, qty: parseInt(it.qty) || 1, unit_price: parseFloat(it.unit_price) || 0 })),
      };
      const { data } = isNew
        ? await axios.post(`${API}/invoices`, payload, { withCredentials: true })
        : await axios.patch(`${API}/invoices/${form.id}`, payload, { withCredentials: true });
      toast.success(`Rechnung ${data.number} gespeichert.`);
      onSaved(data);
      if (printAfter) onPrint(data);
      return data;
    } catch {
      toast.error("Speichern fehlgeschlagen.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const sendByEmail = async () => {
    const saved = await save(false);
    if (!saved) return;
    if (!saved.customer_email) {
      toast.error("Keine E-Mail-Adresse beim Kunden hinterlegt.");
      return;
    }
    try {
      await axios.post(`${API}/invoices/${saved.id}/send`, {}, { withCredentials: true });
      toast.success(`Rechnung ${saved.number} wurde an ${saved.customer_email} gesendet.`);
      onSaved(saved);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "E-Mail-Versand fehlgeschlagen.");
    }
  };

  return (
    <div data-testid="invoice-editor">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <button data-testid="invoice-back" onClick={onClose} className="inline-flex items-center gap-2 border border-white/25 hover:border-white px-5 py-2.5 rounded-full text-sm font-display transition-colors">
          <ArrowLeft size={15} /> Zur Übersicht
        </button>
        <div className="flex flex-wrap gap-3">
          <button data-testid="invoice-save" onClick={() => save(false)} disabled={saving} className="inline-flex items-center gap-2 border border-white/25 hover:border-white px-5 py-2.5 rounded-full text-sm font-display transition-colors">
            <Save size={14} /> {saving ? "Speichere …" : "Speichern"}
          </button>
          <button data-testid="invoice-send-email" onClick={sendByEmail} disabled={saving} className="inline-flex items-center gap-2 border border-[#e2001a]/50 text-[#ff8090] hover:bg-[#e2001a]/10 px-5 py-2.5 rounded-full text-sm font-display transition-colors">
            <Mail size={14} /> Per E-Mail senden
          </button>
          <button data-testid="invoice-save-print" onClick={() => save(true)} disabled={saving} className="inline-flex items-center gap-2 bg-[#e2001a] hover:bg-[#b80016] px-5 py-2.5 rounded-full text-sm font-display font-semibold transition-colors">
            <Printer size={14} /> Speichern & Drucken
          </button>
        </div>
      </div>

      {/* Kunde + Meta */}
      <div className="bg-[#1e2226] border border-white/10 rounded-2xl p-6 mb-6">
        <p className="font-display text-white/40 text-xs uppercase tracking-[0.2em] mb-4">Kunde {form.number && <span className="text-[#e2001a] ml-2">{form.number}</span>}</p>
        {customers.length > 0 && (
          <select data-testid="invoice-customer-pick" value="" onChange={(e) => pickCustomer(e.target.value)} className={`${selectCls} mb-4`}>
            <option value="">Stammkunde wählen ({customers.length}) …</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}{c.offers_count ? ` (${c.offers_count})` : ""}</option>
            ))}
          </select>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="invoice-customer-name" value={form.customer_name} onChange={set("customer_name")} placeholder="Kundenname *" className={inputCls} />
          <input data-testid="invoice-customer-email" type="email" value={form.customer_email} onChange={set("customer_email")} placeholder="E-Mail (optional)" className={inputCls} />
          <textarea data-testid="invoice-customer-address" rows={2} value={form.customer_address} onChange={set("customer_address")} placeholder="Adresse (optional)" className={`${inputCls} resize-none`} />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/40 text-xs">Rechnungsdatum</label>
              <input data-testid="invoice-date" type="date" value={form.invoice_date} onChange={set("invoice_date")} className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-white/40 text-xs">Fällig bis</label>
              <input data-testid="invoice-due" type="date" value={form.due_date} onChange={set("due_date")} className={`${inputCls} mt-1`} />
            </div>
          </div>
          <div>
            <label className="text-white/40 text-xs">Mehrwertsteuer</label>
            <select data-testid="invoice-vat-rate" value={form.vat_rate} onChange={set("vat_rate")} className={`${selectCls} mt-1`}>
              <option value={19}>19 % (Regelsteuersatz)</option>
              <option value={7}>7 % (ermäßigt)</option>
              <option value={0}>0 % (steuerfrei)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Positionen */}
      <p className="font-display text-white/40 text-xs uppercase tracking-[0.2em] mb-4">Positionen</p>
      <div className="space-y-5 mb-6">
        {form.items.map((it, i) => {
          const opts = PRODUCT_OPTIONS[it.product] || [];
          const isFence = it.product === "Zäune";
          return (
            <div key={i} data-testid={`invoice-item-${i}`} className="bg-[#1e2226] border border-white/10 rounded-2xl p-6">
              <div className="grid md:grid-cols-[150px_1fr] gap-5">
                <div className="bg-[#2c3034] rounded-xl p-3 h-fit">
                  <div className="h-28">
                    <ElementPreview bare productName={it.product} color={it.color} opts={it.options} aspect={it.width && it.height ? it.width / it.height : 1} />
                  </div>
                  <p className="text-center text-white/45 text-[11px] mt-2">
                    {isFence ? `${it.fence_length} m · ${it.fence_height}` : it.product === "Dienstleistung / Montage" ? "Dienstleistung" : `${it.width} × ${it.height} mm`}
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-3">
                    <select data-testid={`inv-item-product-${i}`} value={it.product} onChange={(e) => setItem(i, { product: e.target.value, options: {} })} className={`${selectCls} !w-auto`}>
                      {CONFIGURATOR_PRODUCTS.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
                      <option value="Dienstleistung / Montage">Dienstleistung / Montage</option>
                    </select>
                    <select data-testid={`inv-item-color-${i}`} value={it.color} onChange={(e) => setItem(i, { color: e.target.value })} className={`${selectCls} !w-auto`}>
                      {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {form.items.length > 1 && (
                      <button data-testid={`inv-item-delete-${i}`} onClick={() => removeItem(i)} className="ml-auto text-white/30 hover:text-[#ff4d5e] transition-colors p-2" aria-label="Position löschen">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  {isFence ? (
                    <div className="flex flex-wrap gap-3 items-center">
                      <input data-testid={`inv-item-fencelength-${i}`} type="number" min="1" step="0.5" value={it.fence_length} onChange={(e) => setItem(i, { fence_length: e.target.value })} placeholder="Länge m" className={`${inputCls} !w-32`} />
                      <select data-testid={`inv-item-fenceheight-${i}`} value={it.fence_height} onChange={(e) => setItem(i, { fence_height: e.target.value })} className={`${selectCls} !w-auto`}>
                        {["1,0 m", "1,4 m", "1,8 m", "2,0 m"].map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                  ) : it.product !== "Dienstleistung / Montage" && (
                    <div className="flex flex-wrap gap-3">
                      <input data-testid={`inv-item-width-${i}`} type="number" value={it.width} onChange={(e) => setItem(i, { width: e.target.value })} placeholder="Breite mm" className={`${inputCls} !w-32`} />
                      <input data-testid={`inv-item-height-${i}`} type="number" value={it.height} onChange={(e) => setItem(i, { height: e.target.value })} placeholder="Höhe mm" className={`${inputCls} !w-32`} />
                    </div>
                  )}

                  {opts.map((o) => (
                    <select
                      key={o.key}
                      data-testid={`inv-item-opt-${i}-${o.key}`}
                      value={it.options[o.key] || ""}
                      onChange={(e) => setItem(i, { options: { ...it.options, [o.key]: e.target.value } })}
                      className={`${selectCls} !w-auto mr-3`}
                    >
                      <option value="">{o.label} wählen …</option>
                      {o.choices.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ))}

                  <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-white/10">
                    <div>
                      <label className="text-white/40 text-xs">Menge</label>
                      <input data-testid={`inv-item-qty-${i}`} type="number" min="1" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} className={`${inputCls} !w-24 mt-1`} />
                    </div>
                    <div>
                      <label className="text-white/40 text-xs">Einzelpreis (€, netto)</label>
                      <input data-testid={`inv-item-price-${i}`} type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(i, { unit_price: e.target.value })} className={`${inputCls} !w-40 mt-1`} />
                    </div>
                    <button data-testid={`inv-item-suggest-${i}`} onClick={() => suggestPrice(i)} className="inline-flex items-center gap-2 border border-[#e2001a]/50 text-[#ff8090] hover:bg-[#e2001a]/10 px-4 py-3 rounded-xl text-sm font-display transition-colors">
                      <Wand2 size={14} /> Richtpreis übernehmen
                    </button>
                    <span className="ml-auto font-display font-semibold text-white">{eur((parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0))}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button data-testid="invoice-add-item" onClick={addItem} className="inline-flex items-center gap-2 border border-white/25 hover:border-white px-5 py-3 rounded-xl text-sm font-display transition-colors mb-6">
        <Plus size={15} /> Position hinzufügen
      </button>

      {/* Hinweise */}
      <div className="bg-[#1e2226] border border-white/10 rounded-2xl p-6 mb-6">
        <p className="font-display text-white/40 text-xs uppercase tracking-[0.2em] mb-3">Hinweise auf der Rechnung</p>
        <textarea data-testid="invoice-note" rows={3} value={form.note} onChange={set("note")} className={`${inputCls} resize-none`} />
      </div>

      <div className="bg-[#1e2226] border border-[#e2001a]/40 rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-3">
          {INVOICE_STATUSES.map(([k, label]) => (
            <button
              key={k}
              data-testid={`invoice-status-${k}`}
              onClick={() => setForm({ ...form, status: k })}
              className={`px-4 py-2 rounded-full border font-display text-xs font-medium transition-colors ${
                form.status === k ? "bg-[#e2001a] border-[#e2001a] text-white" : "border-white/20 text-white/55 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="font-display text-white/60 text-sm">
          Netto {eur(totalNet)} · <span className="text-white/80">Gesamt{rate ? ` inkl. ${rate} % MwSt.` : " (steuerfrei)"}:</span> <span data-testid="invoice-total" className="text-[#e2001a] font-bold text-2xl ml-1">{eur(totalGross)}</span>
        </p>
      </div>

      {/* Vorschau */}
      <p className="font-display text-white/40 text-xs uppercase tracking-[0.2em] mt-10 mb-4">Vorschau (so sieht die Rechnung aus)</p>
      <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
        <InvoiceSheet invoice={{ ...form, vat_rate: rate, number: form.number || "RE-…", timestamp: form.timestamp || new Date().toISOString() }} />
      </div>
    </div>
  );
};
